import { useCallback, useEffect, useMemo, useState } from "react";
import { useTableSort, SortTh } from "@/lib/tableSort";

import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye } from "lucide-react";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { ArchiveInfraButton } from "@/components/network/ArchiveInfraButton";
import { SplitterRelations } from "@/components/network/SplitterRelations";
import { InfraFormDialog } from "@/components/network/InfraFormDialog";
import { OdpPortsPanel } from "@/components/network/OdpPortsPanel";
import { StatusPill, NoCoordBadge, PortBar, LocCell, RelocateHint } from "@/components/network/StatusPill";
import { ServerInfo } from "@/components/network/ServerInfo";
import { OdpSplitters } from "@/components/network/OdpSplitters";

import { InfraFilters, ScopeBanner } from "@/pages/NetworkOdc";
import { useNetworkRefs, canManageWilayah, canEditInfra, SELECT_CLS } from "@/lib/network";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { InfraCards, InfraCard } from "@/components/network/InfraCards";

function OdpDetail({ odp, me, wilayahName, onClose, onChanged, onDeleted }) {
  return (
    <Dialog open={!!odp} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[calc(100dvh-5.5rem)] overflow-y-auto max-lg:top-[calc(50%+2rem)] lg:max-h-[92vh]" data-testid="odp-detail-dialog">
        <DialogHeader><DialogTitle className="break-words pr-6" data-testid="odp-detail-title">{odp?.kode} — {odp?.nama}</DialogTitle></DialogHeader>
        {odp && (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-3 text-xs">
              <StatusPill status={odp.status} /><span>ODC: <b>{odp.odc_kode || "-"}</b></span><span>Wilayah: {wilayahName(odp.wilayah_id)}</span>
              {odp.tanggal_pasang && <span>Pasang: {odp.tanggal_pasang}</span>}{(odp.in_server || !odp.has_coords) && <LocCell row={odp} testid="odp-detail-loc" />}
              <span className="ml-auto flex gap-2"><ArchiveInfraButton kind="odp" item={odp} me={me} onDone={onDeleted} /><DeleteInfraButton kind="odp" item={odp} me={me} onDeleted={onDeleted} /></span>
            </div>
            <ServerInfo kind="odp" id={odp.id} />
            <OdpSplitters odpId={odp.id} me={me} onChanged={onChanged} />
            <RelocateHint show={!odp.in_server && !odp.has_coords && !!odp.odc_id} testid="odp-relocate-hint" />
            {(odp.alamat || odp.has_coords) && <div className="text-xs text-[#6B7280]" data-testid="odp-detail-address">{[odp.alamat, odp.has_coords && `${odp.latitude}, ${odp.longitude}`].filter(Boolean).join(" · ")}</div>}
            <SplitterRelations kind="odp" id={odp.id} kode={odp.kode} />
            {!canManageWilayah(me, odp.wilayah_id) && <p className="text-xs text-[#6B7280]" data-testid="odp-readonly-note">Penempatan pelanggan: mode lihat saja (ODP di luar wilayah kewenangan Anda).</p>}
            <OdpPortsPanel odpId={odp.id} me={me} onChanged={onChanged} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function NetworkOdp() {
  const { refs, wilayahName, lokasiName } = useNetworkRefs();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", archived: false, odc_id: sp.get("odc_id") || "" });
  const [rows, setRows] = useState(null);
  const [odcs, setOdcs] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get("/network/odp", { params: f }).then((r) => setRows(r.data)), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const [odcArch, setOdcArch] = useState([]);
  useEffect(() => { api.get("/network/odc").then((r) => setOdcs(r.data)); api.get("/network/odc", { params: { archived: true } }).then((r) => setOdcArch(r.data)); }, []);
  const odcKode = useCallback((id) => (odcs.find((o) => o.id === id) || odcArch.find((o) => o.id === id))?.kode, [odcs, odcArch]);
  const getters = useMemo(() => ({ kode: (o) => o.kode, nama: (o) => o.nama, odc: (o) => odcKode(o.odc_id), wilayah: (o) => wilayahName(o.wilayah_id),
    lokasi: (o) => lokasiName(o.lokasi_id), port: (o) => (o.kapasitas_port ? o.port_terpakai / o.kapasitas_port : null), status: (o) => o.status }),
  [odcKode, wilayahName, lokasiName]);
  const st = useTableSort("odp", rows, getters);
  const me = refs?.me;
  useEffect(() => {
    const id = sp.get("odp_id");
    if (id && rows) { const o = rows.find((x) => x.id === id); if (o) setDetail(o); }
  }, [sp, rows]);
  if (me && !me.can_manage) return <Navigate to="/network/map" replace />;
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-odp-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master ODP</h1>
          <p className="text-sm text-[#6B7280]">Kapasitas port dan penempatan pelanggan</p></div>
        {me?.can_manage && <Button onClick={() => setForm({})} data-testid="odp-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah ODP</Button>}
      </div>
      <ScopeBanner me={me} />
      <InfraFilters refs={refs} f={f} setF={setF} prefix="odp">
        <select className={SELECT_CLS} value={f.odc_id} onChange={(e) => setF((x) => ({ ...x, odc_id: e.target.value }))} data-testid="odp-filter-odc">
          <option value="">Semua ODC Induk</option>
          <option value="__none__">— Tanpa ODC Induk —</option>
          {odcs.map((o) => <option key={o.id} value={o.id}>{o.kode}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs" data-testid="odp-filter-archived">
          <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="odp-filter-archived-toggle" />Tampilkan Arsip</label>
      </InfraFilters>
      <InfraCards prefix="odp" st={st} cols={[["kode", "Kode"], ["nama", "Nama"], ["odc", "ODC"], ["wilayah", "Wilayah"], ["lokasi", "Lokasi"], ["port", "Port Terpakai"], ["status", "Status"]]}
                  empty={rows && !rows.length ? (f.archived ? "Tidak ada ODP yang diarsipkan." : "Belum ada ODP.") : null}
                  render={(o) => (
        <InfraCard key={o.id} prefix="odp" row={o} q={f.q}
                   sub={<>ODC {odcKode(o.odc_id) || "-"} · <span className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</span> · {lokasiName(o.lokasi_id)}</>}
                   info={<PortBar used={o.port_terpakai} cap={o.kapasitas_port} testid={`odp-card-bar-${o.kode}`} />}
                   actions={<>
                     <ArahkanButton iconOnly lat={o.latitude} lng={o.longitude} testid={`odp-card-nav-${o.kode}`} />
                     <Button size="icon" variant="ghost" onClick={() => setDetail({ ...o, odc_kode: odcKode(o.odc_id) })} data-testid={`odp-card-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                     <ArchiveInfraButton iconOnly kind="odp" item={o} me={me} onDone={load} tid={`odp-card-${o.archived ? "restore" : "archive"}-${o.kode}`} />
                     {canEditInfra(me) && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`odp-card-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                     <DeleteInfraButton iconOnly kind="odp" item={o} me={me} onDeleted={load} tid={`odp-card-delete-${o.kode}`} />
                   </>} />)} />
      <div className="hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white md:block">
        <table className="w-full text-sm" data-testid="odp-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr><SortTh className="p-2" col="kode" label="Kode" s={st} testid="odp" /><SortTh col="nama" label="Nama" s={st} testid="odp" />
              <SortTh col="odc" label="ODC" s={st} testid="odp" /><SortTh col="wilayah" label="Wilayah" s={st} testid="odp" />
              <SortTh col="lokasi" label="Lokasi" s={st} testid="odp" /><SortTh col="port" label="Port Terpakai" s={st} testid="odp" />
              <SortTh col="status" label="Status" s={st} testid="odp" /><th>Peta</th><th className="pr-2 text-right">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((o) => (
              <tr key={o.id} className="border-t" data-testid={`odp-row-${o.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{o.kode}<OldKodeLabel row={o} q={f.q} testid={`odp-oldkode-${o.kode}`} />{o.archived && <span className="ml-1 rounded bg-[#E5E7EB] px-1 text-[10px] font-semibold text-[#374151]" data-testid={`odp-archived-badge-${o.kode}`}>ARSIP</span>}</td><td>{o.nama}</td><td>{odcKode(o.odc_id) || "-"}</td>
                <td className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</td><td>{lokasiName(o.lokasi_id)}</td>
                <td><PortBar used={o.port_terpakai} cap={o.kapasitas_port} testid={`odp-bar-${o.kode}`} /></td>
                <td><StatusPill status={o.status} /></td><td><LocCell row={o} testid={`odp-nocoord-${o.kode}`} /></td>
                <td className="whitespace-nowrap pr-2 text-right">
                  <ArahkanButton iconOnly lat={o.latitude} lng={o.longitude} testid={`odp-nav-${o.kode}`} />
                  <Button size="icon" variant="ghost" onClick={() => setDetail({ ...o, odc_kode: odcKode(o.odc_id) })} data-testid={`odp-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                  <ArchiveInfraButton iconOnly kind="odp" item={o} me={me} onDone={load} />
                  {canEditInfra(me) && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`odp-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                  <DeleteInfraButton iconOnly kind="odp" item={o} me={me} onDeleted={load} />
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={9} className="p-6 text-center text-[#6B7280]">{f.archived ? "Tidak ada ODP yang diarsipkan." : "Belum ada ODP."}</td></tr>}
          </tbody>
        </table>
      </div>
      <InfraFormDialog kind="odp" open={!!form} editing={form?.id ? form : null} refs={refs} odcList={odcs}
                       onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      <OdpDetail odp={detail} me={me} wilayahName={wilayahName} onClose={() => setDetail(null)} onChanged={load} onDeleted={() => { setDetail(null); load(); }} />
    </div>
  );
}
